import { FetchApi } from '@backstage/core-plugin-api';
import type { SaSreChatApi } from './SaSreChatApi';
import type {
  ChatMessage,
  ChatArtifact,
  SreTask,
  SreExpert,
  FunctionType,
  SecurityAlert,
  SseChunk,
} from '../types';
import { MOCK_SCENARIOS, MOCK_TASKS, MOCK_EXPERTS } from './mockResponses';

const PROXY_BASE = '/api/proxy/superagent-sre';

/** バックエンド接続のタイムアウト（ms） */
const API_TIMEOUT_MS = 12_000;

/**
 * SA_SRE Chat API Client
 *
 * Communicates with superagent-sre through the Backstage backend proxy.
 * Handles SSE streaming for real-time chat responses.
 *
 * バックエンド不達時はデモレスポンスにフォールバックし、
 * ストリーミング風の段階的メッセージ配信をシミュレートする。
 */
export class SaSreChatClient implements SaSreChatApi {
  private readonly fetchApi: FetchApi;

  /** 現在のアクティブ機能（フォールバック時に使用） */
  private currentFunction: FunctionType = 'security_alert';

  constructor(options: { fetchApi: FetchApi }) {
    this.fetchApi = options.fetchApi;
  }

  /** 外部から現在の機能タイプを設定（ChatContext から呼ばれる） */
  setCurrentFunction(fn: FunctionType): void {
    this.currentFunction = fn;
  }

  async sendMessage(
    message: string,
    activeTask: string | null,
    experts: string[],
    onChunk: (chunk: {
      type: 'message' | 'artifact';
      message?: ChatMessage;
      artifact?: ChatArtifact;
    }) => void,
    file?: File,
  ): Promise<void> {
    try {
      const body = file
        ? this.buildFormData(message, activeTask, experts, file)
        : JSON.stringify({
            message,
            activeTask,
            experts: JSON.stringify(experts),
          });

      const headers: Record<string, string> = file
        ? {}
        : { 'Content-Type': 'application/json' };

      // AbortController でタイムアウトを実装
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      try {
        const response = await this.fetchApi.fetch(
          `${PROXY_BASE}/api/chat/message`,
          { method: 'POST', headers, body, signal: controller.signal },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(
            `Chat API error: ${response.status} ${response.statusText}`,
          );
        }

        await this.consumeSseStream(response, onChunk);
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        // フェッチ失敗 → デモレスポンスにフォールバック
        await this.streamMockResponse(onChunk);
      }
    } catch {
      // 最後のフォールバック
      await this.streamMockResponse(onChunk);
    }
  }

  async getSuggestions(
    activeTask: string | null,
    artifactContent?: string,
  ): Promise<string[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      const response = await this.fetchApi.fetch(
        `${PROXY_BASE}/api/chat/suggestions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ activeTask, artifactContent }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);
      if (!response.ok) return this.getMockSuggestions();
      const data = await response.json();
      return data.suggestions ?? this.getMockSuggestions();
    } catch {
      return this.getMockSuggestions();
    }
  }

  async getTasks(functionType: FunctionType): Promise<SreTask[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      const response = await this.fetchApi.fetch(
        `${PROXY_BASE}/api/master/tasks?functionType=${functionType}`,
        { signal: controller.signal },
      );

      clearTimeout(timeoutId);
      if (!response.ok) return [...MOCK_TASKS[functionType]];
      const data = await response.json();
      const tasks = data.tasks ?? data ?? [];
      return tasks.length > 0 ? tasks : [...MOCK_TASKS[functionType]];
    } catch {
      return [...MOCK_TASKS[functionType]];
    }
  }

  async getExperts(functionType: FunctionType): Promise<SreExpert[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      const response = await this.fetchApi.fetch(
        `${PROXY_BASE}/api/master/experts?functionType=${functionType}`,
        { signal: controller.signal },
      );

      clearTimeout(timeoutId);
      if (!response.ok) return [...MOCK_EXPERTS[functionType]];
      const data = await response.json();
      const experts = data.experts ?? data ?? [];
      return experts.length > 0 ? experts : [...MOCK_EXPERTS[functionType]];
    } catch {
      return [...MOCK_EXPERTS[functionType]];
    }
  }

  async getDashboard(): Promise<Record<string, unknown>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      const response = await this.fetchApi.fetch(
        `${PROXY_BASE}/api/sre/dashboard`,
        { signal: controller.signal },
      );

      clearTimeout(timeoutId);
      if (!response.ok) return {};
      return response.json();
    } catch {
      return {};
    }
  }

  async getAlerts(params?: {
    provider?: string;
    severity?: string;
    status?: string;
    limit?: number;
  }): Promise<SecurityAlert[]> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.provider) searchParams.set('provider', params.provider);
      if (params?.severity) searchParams.set('severity', params.severity);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.limit) searchParams.set('limit', String(params.limit));

      const query = searchParams.toString();
      const url = `${PROXY_BASE}/api/sre/alerts${query ? `?${query}` : ''}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      const response = await this.fetchApi.fetch(url, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      if (!response.ok) return [];
      const data = await response.json();
      return data.data ?? [];
    } catch {
      return [];
    }
  }

  async triggerAnalysis(action: {
    id: string;
    title: string;
    domain: string;
    type: string;
    context?: Record<string, unknown>;
  }): Promise<{ report: string; generatedAt: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

    try {
      const response = await this.fetchApi.fetch(
        `${PROXY_BASE}/api/sre/agent-analysis`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Analysis API error: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch {
      clearTimeout(timeoutId);
      return {
        report: `# ${action.title}\n\n分析レポートはバックエンド接続時に生成されます。`,
        generatedAt: new Date().toISOString(),
      };
    }
  }

  // ── Private helpers ──────────────────────────────────────

  private buildFormData(
    message: string,
    activeTask: string | null,
    experts: string[],
    file: File,
  ): FormData {
    const formData = new FormData();
    formData.append('message', message);
    if (activeTask) formData.append('activeTask', activeTask);
    formData.append('experts', JSON.stringify(experts));
    formData.append('file', file);
    return formData;
  }

  /**
   * Consumes an SSE stream from superagent-sre and dispatches parsed chunks.
   */
  private async consumeSseStream(
    response: Response,
    onChunk: (chunk: {
      type: 'message' | 'artifact';
      message?: ChatMessage;
      artifact?: ChatArtifact;
    }) => void,
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr || jsonStr === '[DONE]') continue;

          try {
            const chunk = JSON.parse(jsonStr) as SseChunk;
            if (chunk.type === 'message' && chunk.message) {
              onChunk({
                type: 'message',
                message: {
                  id: crypto.randomUUID(),
                  timestamp: new Date(),
                  ...chunk.message,
                } as ChatMessage,
              });
            } else if (chunk.type === 'artifact' && chunk.artifact) {
              onChunk({
                type: 'artifact',
                artifact: {
                  id: crypto.randomUUID(),
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  ...chunk.artifact,
                } as ChatArtifact,
              });
            }
          } catch {
            // Skip malformed JSON chunks
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * デモレスポンスをストリーミング風に段階配信する。
   *
   * ux-psychology:
   *   Doherty Threshold — 各メッセージ間を 800-1500ms で配信
   *   Progressive Disclosure — PMO → Agent1 → Agent2 → Artifacts の順
   */
  private async streamMockResponse(
    onChunk: (chunk: {
      type: 'message' | 'artifact';
      message?: ChatMessage;
      artifact?: ChatArtifact;
    }) => void,
  ): Promise<void> {
    const scenario = MOCK_SCENARIOS[this.currentFunction];
    const delay = (ms: number) =>
      new Promise<void>(resolve => setTimeout(resolve, ms));

    // 1. PMO メッセージ（800ms 後）
    await delay(800);
    onChunk({
      type: 'message',
      message: {
        id: crypto.randomUUID(),
        sender: 'pmo',
        content: scenario.pmoMessage,
        timestamp: new Date(),
        functionContext: this.currentFunction,
      },
    });

    // 2. エージェントメッセージ（各 1200-1800ms 間隔）
    for (const agentMsg of scenario.agentMessages) {
      await delay(1200 + Math.random() * 600);
      onChunk({
        type: 'message',
        message: {
          id: crypto.randomUUID(),
          sender: 'agent',
          agentRole: agentMsg.agentRole,
          content: agentMsg.content,
          timestamp: new Date(),
          functionContext: this.currentFunction,
        },
      });
    }

    // 3. 成果物（各 500ms 間隔）
    for (const artifact of scenario.artifacts) {
      await delay(500);
      onChunk({
        type: 'artifact',
        artifact: {
          id: crypto.randomUUID(),
          type: artifact.type,
          name: artifact.name,
          content: artifact.content,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'completed',
        },
      });
    }
  }

  /** デモ用サジェスチョンを返す */
  private getMockSuggestions(): string[] {
    const scenario = MOCK_SCENARIOS[this.currentFunction];
    return [...scenario.suggestions];
  }
}
